import React                        from 'react';
import NumberFormat                 from 'react-number-format';
import moment                       from 'moment';
import { AxiosError }               from 'axios';
import { ICheckboxOption }          from '@components/Checkbox/CheckboxFieldGroup';
import { IBid, IAuction }           from '@typings/auction';
import {
  IRecord,
  ISearchResult,
}                                   from '@typings/search';
import { EAuctionStatus }           from '@enums/auction';
import { EMonthNumber }             from '@enums/common';
import { MONTH_FULLNAME_BY_NUMBER } from './suggestions';

export const capitalize = (string: string): string =>
  `${string.charAt(0).toUpperCase()}${string.slice(1)}`;

export const addingTimezone = (value?: string): string =>
  (value && !value.endsWith('Z')
    ? `${value}Z`
    : value ?? ''
  );

export const formatAuctionDate = (
  date?: string,
  format = 'MM/DD/YY hh:mm A z',
): string => {
  const momentDate = moment(addingTimezone(date));

  return momentDate.isValid()
    ? momentDate.format(format)
    : 'Unscheduled';
};

const getNumberValue = (value: number | null): number | string => {
  switch (value) {
    case Infinity:
      return 0;
    case null:
      return '';
    default:
      return value;
  }
};

export const formatFeeValue = (
  value              = 0,
  withCurrencySymbol = true,
): string => {
  const localedValue = Math.abs(value).toLocaleString('en-US', {
    style    : 'currency',
    currency : 'USD',
  });

  const withSymbolValue = withCurrencySymbol ? localedValue : localedValue.substr(1);

  return value < 0 ? `(${withSymbolValue})` : withSymbolValue;
};

export const formatCurrency = (
  value: number | null,
  fixedDecimalScale = true,
): JSX.Element => {
  const val = getNumberValue(value);

  return (
    <NumberFormat
      value             = {val}
      prefix            = "$"
      displayType       = "text"
      decimalScale      = {2}
      fixedDecimalScale = {fixedDecimalScale}
      thousandSeparator
    />
  );
};

export const flattenArray = ([first, ...rest]: any[]): any[] => {
  if (first === undefined) {
    return [];
  }

  if (!Array.isArray(first)) {
    return [first, ...flattenArray(rest)];
  }

  return [...flattenArray(first), ...flattenArray(rest)];
};

export const formatTitle = (
  item: IAuction | IBid,
): string => {
  const year         = item?.vehicle?.year ?? '';
  const carBrandName = item?.vehicle?.carBrandName ?? '';
  const carModelName = item?.vehicle?.carModelName ?? '';

  return `${year} ${carBrandName} ${carModelName}`.trim();
};

interface IUserNameInfo {
  firstName? : string;
  lastName?  : string;
}
export const formatFullName = (userInfo: IUserNameInfo): string => {
  const name    = userInfo.firstName ?? '';
  const surname = userInfo.lastName ?? '';

  return `${name} ${surname}`.trim();
};

export const getRetailPercentage = (auction: IAuction): number => {
  const { minimumBid, currentHighestBid, retailValue } = auction ?? {};
  const bidValue = currentHighestBid || minimumBid;

  return (bidValue && retailValue) ? +((bidValue * 100) / retailValue).toFixed() : 0;
};

export const getWholesalePercentage = (auction: IAuction): number => {
  const { minimumBid, currentHighestBid, wholesaleValue } = auction ?? {};
  const bidValue = currentHighestBid || minimumBid;

  return (bidValue && wholesaleValue) ? +((bidValue * 100) / wholesaleValue).toFixed() : 0;
};

export const deepEqual = (x: any, y: any): boolean => {
  const ok = Object.keys;
  const tx = typeof x;
  const ty = typeof y;

  return x && y && tx === 'object' && tx === ty ? (
    ok(x).length === ok(y).length
      && ok(x).every((key): boolean => deepEqual(x[key], y[key]))
  ) : (x === y);
};

type TSortDirection = 1 | -1;

export const multiFieldSorter = (options: string[]): any => {
  const direction: TSortDirection[] = [];
  const { length } = options;

  const fields = options.map(
    (option, idx) => {
      let opt;

      if (option[0] === '-') {
        direction[idx] = -1;
        opt = option.substring(1);
      } else {
        direction[idx] = 1;
        opt = option;
      }

      return opt;
    },
  );

  return (a: any, b: any): number => {
    for (let i = 0; i < length; ++i) {
      const field = fields[i];

      if (a[field] > b[field]) return direction[i];
      if (a[field] < b[field]) return -(direction[i]);
    }

    return 0;
  };
};

export const isBeforeNow = (date: string): boolean => {
  const momentDate = moment(addingTimezone(date));
  const momentNow = moment();

  return momentDate.isValid() && momentDate.isBefore(momentNow);
};

export const checkUrlProtocol = (url: string): string =>
  (url.match(/^http[s]?:\/\//) ? url : `https://${url}`);

export const showErrorMessage = (error: AxiosError): string => {
  const { data } = error.response ?? {};

  if (!data) return 'Error happened...';

  return data.description ?? data.error_description ?? data.map((e: any): string[] => e.errorMessage).join(' ');
};

export const getIsShowBidList = (status: EAuctionStatus) => {
  const statusesWithBids: EAuctionStatus[] = [
    EAuctionStatus.Active,
    EAuctionStatus.Archived,
    EAuctionStatus.Ended,
  ];

  return statusesWithBids.includes(status);
};

export const getIsShownDetails = (status: EAuctionStatus) => {
  const statusesWithoutDetails: EAuctionStatus[] = [
    EAuctionStatus.SavedDraft,
    EAuctionStatus.SavedDraftNoPhotos,
  ];

  return !statusesWithoutDetails.includes(status);
};

export function parseRecords<TResult = unknown>(searchResult: ISearchResult<TResult>): TResult[] {
  return (searchResult.records ?? []).map((record: IRecord<TResult>) => record.record);
}

export const getCurrentMonthName = (): string => {
  const currentMonth: EMonthNumber = moment().month() + 1;

  return MONTH_FULLNAME_BY_NUMBER[currentMonth];
};

export const getFormattedDate = (
  dateString   = '',
  format       = 'MM/DD/YY hh:mm a',
  defaultValue = '',
): string => {
  const date = moment(addingTimezone(dateString)).format(format);

  return date !== 'Invalid date' ? date : defaultValue;
};

export const formatParsedZoneDate = (
  dateString   = '',
  format       = 'LL',
  defaultValue = '',
) => (!dateString ? defaultValue : moment.parseZone(dateString).format(format));

export const isObjectEmpty = (
  someObject: object | null,
): boolean => !(someObject && Object.keys(someObject).length);

export function getValuesFromCheckboxOptions<TName extends string = string>(
  options: ICheckboxOption<TName>[],
): Record<TName, boolean> {
  return options.reduce(
    (acc: Record<TName, boolean>, option: ICheckboxOption) => ({
      ...acc,
      [option.name]: false,
    }),
    {} as Record<TName, boolean>,
  );
}
